import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { PDF_CONFIG } from '@/utils/pdfConfig';
import PDFSidebar from './PDFSidebar';
import NoteEditModal from './PDFSectionViewer/components/NoteEditModal';
import PDFViewerHeader from './PDFSectionViewer/components/PDFViewerHeader';
import PDFTextActions from './PDFSectionViewer/components/PDFTextActions';
import PDFDocumentContent from './PDFSectionViewer/components/PDFDocumentContent';
import PDFPageStack from './PDFSectionViewer/components/PDFPageStack';
import PDFLoadingState from './PDFSectionViewer/components/PDFLoadingState';
import PDFErrorState from './PDFSectionViewer/components/PDFErrorState';
import useLearningPackState from './PDFSectionViewer/hooks/useLearningPackState';
import usePdfTextActions from './PDFSectionViewer/hooks/usePdfTextActions';
import usePdfZoomControls from './PDFSectionViewer/hooks/usePdfZoomControls';
import usePdfSearch from './PDFSectionViewer/hooks/usePdfSearch';
import usePdfFullscreen from './PDFSectionViewer/hooks/usePdfFullscreen';
import usePdfPageInput from './PDFSectionViewer/hooks/usePdfPageInput';
import useAutoEditNote from './PDFSectionViewer/hooks/useAutoEditNote';
import useTargetSectionNavigation from './PDFSectionViewer/hooks/useTargetSectionNavigation';
import useTargetAnnotationNavigation from './PDFSectionViewer/hooks/useTargetAnnotationNavigation';
import useTargetFigureNavigation from './PDFSectionViewer/hooks/useTargetFigureNavigation';
import useHighlightedHighlightNavigation from './PDFSectionViewer/hooks/useHighlightedHighlightNavigation';
import usePdfDocumentLifecycle from './PDFSectionViewer/hooks/usePdfDocumentLifecycle';
import usePdfSectionNavigation from './PDFSectionViewer/hooks/usePdfSectionNavigation';
import usePdfHighlightStyles from './PDFSectionViewer/hooks/usePdfHighlightStyles';
import usePdfHighlightRemoval from './PDFSectionViewer/hooks/usePdfHighlightRemoval';
import usePdfSelectionInteractions from './PDFSectionViewer/hooks/usePdfSelectionInteractions';
import usePdfInitialPageNavigation from './PDFSectionViewer/hooks/usePdfInitialPageNavigation';
import usePdfHighlightApplication from './PDFSectionViewer/hooks/usePdfHighlightApplication';
import usePdfSelectionAnchor from './PDFSectionViewer/hooks/usePdfSelectionAnchor';
import usePdfAutoScale from './PDFSectionViewer/hooks/usePdfAutoScale';
import usePdfPageLoadHandler from './PDFSectionViewer/hooks/usePdfPageLoadHandler';
import usePdfNotesIndex from './PDFSectionViewer/hooks/usePdfNotesIndex';
import usePdfDebugLogs from './PDFSectionViewer/hooks/usePdfDebugLogs';
import usePdfSidebarNavigation from './PDFSectionViewer/hooks/usePdfSidebarNavigation';
import findAndScrollToSection from './PDFSectionViewer/utils/findAndScrollToSection';
import { userAnnotationPositionsAPI } from '@/api/userAnnotationPositions';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configure PDF.js worker (import config to ensure it's initialized)
import '@/utils/pdfConfig';

export default function PDFSectionViewer({
  pdfUrl,
  authToken,
  onClose,
  initialPage = 1,
  chapterId = null,
  prefetchedSections = null,
  prefetchedNumPages = null,
  onAddNote = null,
  onViewNotes = null,
  onViewHighlights = null,
  notes = [],
  notePlacementMode = false,
  onPDFClick = null,
  onNoteUpdate = null,
  onNoteDelete = null,
  onNotePageChange = null,
  onNotesReload = null,
  autoEditNote = null,
  onNoteEditComplete = null,
  chapterAnnotations = [],
  userAnnotationPositions = {},
  onActivityClick = null,
  onAskBudleeAI = null,
  highlights = [],
  addHighlight = null,
  removeHighlight = null,
  onShowLearningPack = null,
  learningPackActivities = [],
  onAnnotationPositionUpdate = null,
  isActivityCompleted = null, // Function to check if activity is completed: (activityId) => boolean
  highlightedHighlightId = null, // ID of highlight to scroll to and highlight
  renderTextActions = null,
  targetSection = null,
  targetFigure = null,
  targetAnnotationId = null,
  activeAnnotationId = null,
  onClearTargetAnnotation = null,
  onClearActiveAnnotation = null,
  contentPaddingClass = 'p-4',
  pageAlignment = 'center',
  pageSpacingClass = 'mb-4',
  containerBgClass = 'bg-gray-100',
  containerClass = '',
  onSectionScroll = null, // Callback when PDF scrolls to a new section: (sectionInfo) => void
  onBookmarkChange = null,
  activeBookmarks = {},
}) {
  // Route all PDF loading through backend cached endpoint.
  // This enables byte-range streaming + server-side caching even when the original PDF is in protected storage.
  // NOTE: Bypassing this for now to resolve 504 Gateway Timeout issues in local development.
  const cachedPdfUrl = null;
  /*
  const cachedPdfUrl = useMemo(() => {
    if (!pdfUrl) return null;
    const token = String(authToken || '').trim();
    const base = `/api/core/cache/pdf/?url=${encodeURIComponent(pdfUrl)}`;
    return token ? `${base}&auth_token=${encodeURIComponent(token)}` : base;
  }, [pdfUrl, authToken]);
  */

  // Prefer cached endpoint when available.
  const effectivePdfUrl = cachedPdfUrl || pdfUrl;

  const [numPages, setNumPages] = useState(null);
  const [sections, setSections] = useState([]);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState(null);
  const [workerReady, setWorkerReady] = useState(false);
  const [pageScale, setPageScale] = useState(1.0); // Dynamic scale for fit-to-width
  const [manualZoom, setManualZoom] = useState(false); // Track if user is manually zooming
  const [zoomLevel, setZoomLevel] = useState(100); // Zoom level in percentage (100% = fit to width)
  const [isEditingZoom, setIsEditingZoom] = useState(false); // Track if zoom input is being edited
  const [zoomInputValue, setZoomInputValue] = useState('100'); // Temporary value for zoom input
  const containerRef = useRef(null);
  const viewerRootRef = useRef(null);
  const { isFullscreen, toggleFullscreen } = usePdfFullscreen({ viewerRootRef });
  const pageRefs = useRef({});
  const textLayerReadyPagesRef = useRef({});
  const autoScrollRequestedRef = useRef(false); // Prevent scroll bounce when currentPage updates from user scrolling

  const pageJustifyClass = pageAlignment === 'start' ? 'justify-start' : 'justify-center';
  const pdfDocumentRef = useRef(null); // Store the PDF document from parser
  const scaleCalculatedRef = useRef(false); // Track if scale has been calculated
  const originalPageWidthRef = useRef(null); // Store original page width for resize calculations
  const originalPageHeightRef = useRef(null); // Store original page height for resize calculations
  const baseScaleRef = useRef(1.0); // Store the base scale (fit-to-width) for zoom calculations
  const [editingNote, setEditingNote] = useState(null);
  const pageDimensions = useRef({}); // Store page dimensions for position calculations
  const [highlightedNoteId, setHighlightedNoteId] = useState(null); // For highlighting notes when navigating
  const [positionUpdateTrigger, setPositionUpdateTrigger] = useState(0); // Force re-render when scale changes or positions update
  const [selectedText, setSelectedText] = useState(''); // Selected text in PDF
  const [selectionAnchor, setSelectionAnchor] = useState(null); // Anchor metadata for selected text (page/context)
  const [showTextActions, setShowTextActions] = useState(false); // Show Ask BudleeAI/Highlight buttons
  const [isReading, setIsReading] = useState(false); // Track if text-to-speech is active
  const [textActionPosition, setTextActionPosition] = useState({ top: 0, left: 0 }); // Position for action buttons

  // Visual selection overlay (more reliable than native selection highlight on PDF.js transparent text layer)
  const [selectionRects, setSelectionRects] = useState([]); // [{ top, left, width, height }]
  const selectionRangeRef = useRef(null);
  const textActionsRef = useRef(null); // Ref for text action buttons container
  const isReadingRef = useRef(false); // Synchronous access to reading state

  // PDF Tools State
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [pdfDocumentProxy, setPdfDocumentProxy] = useState(null); // Store PDF proxy for sidebar
  const isProgrammaticScrollRef = useRef(false);
  const {
    pageInputValue,
    handlePageInputChange,
    handlePageInputSubmit,
  } = usePdfPageInput({
    currentPage,
    numPages,
    setCurrentPage,
    pageRefs,
  });

  const {
    isSearchOpen,
    searchQuery,
    setSearchQuery,
    searchResults,
    currentSearchIndex,
    isSearching,
    toggleSearch,
    handleNextResult,
    handlePrevResult,
    highlightMatches,
  } = usePdfSearch({
    pdfDocumentProxy,
    setCurrentPage,
    pageRefs,
  });

  usePdfDebugLogs({ targetFigure, notes });

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const clearTextSelection = useCallback((source = 'unknown', force = false) => {
    console.log('🧹 clearTextSelection called from', source, 'force:', force, 'isReadingRef.current:', isReadingRef.current);

    // While reading aloud, never clear selection unless explicitly forced
    if (!force && isReadingRef.current) {
      console.log('⏸️ Skipping clearTextSelection because we are currently reading');
      return;
    }

    setShowTextActions(false);
    setSelectedText('');
    setSelectionAnchor(null);
    setSelectionRects([]);
    selectionRangeRef.current = null;
    try {
      window.getSelection()?.removeAllRanges();
    } catch { }
  }, []);

  const buildSelectionAnchorFromRange = usePdfSelectionAnchor({ currentPage });

  // Update trigger when user annotation positions change to force re-render with correct positions
  useEffect(() => {
    if (userAnnotationPositions && Object.keys(userAnnotationPositions).length > 0) {
      setPositionUpdateTrigger(prev => prev + 1);
    }
  }, [userAnnotationPositions]);

  useTargetAnnotationNavigation({
    targetAnnotationId,
    chapterAnnotations,
    onClearTargetAnnotation,
    isProgrammaticScrollRef,
    setCurrentPage,
    pageRefs,
    containerRef,
  });

  useAutoEditNote({
    autoEditNote,
    pageRefs,
    containerRef,
    setHighlightedNoteId,
    setEditingNote,
  });

  useTargetSectionNavigation({
    targetSection,
    sections,
    pageRefs,
    setCurrentPage,
    isProgrammaticScrollRef,
  });

  const { activeFigureHighlight } = useTargetFigureNavigation({
    targetFigure,
    pdfDocumentProxy,
    pageRefs,
    containerRef,
    textLayerReadyPagesRef,
    isProgrammaticScrollRef,
    setCurrentPage,
  });

  useHighlightedHighlightNavigation({
    highlightedHighlightId,
    highlights,
    setCurrentPage,
    pageRefs,
    containerRef,
  });

  const { handleDocumentLoadSuccess } = usePdfDocumentLifecycle({
    effectivePdfUrl,
    cachedPdfUrl,
    authToken,
    chapterId,
    prefetchedSections,
    prefetchedNumPages,
    workerReady,
    setWorkerReady,
    setLoading,
    setError,
    setParsing,
    setNumPages,
    setSections,
    setCurrentSectionIndex,
    setCurrentPage,
    setPageScale,
    setZoomLevel,
    setZoomInputValue,
    setManualZoom,
    setIsEditingZoom,
    setPdfDocumentProxy,
    scaleCalculatedRef,
    originalPageWidthRef,
    baseScaleRef,
    pdfDocumentRef,
  });

  usePdfAutoScale({
    numPages,
    containerRef,
    scaleCalculatedRef,
    originalPageWidthRef,
    baseScaleRef,
    manualZoom,
    zoomLevel,
    setPageScale,
    setZoomLevel,
    setZoomInputValue,
  });

  usePdfInitialPageNavigation({
    autoScrollRequestedRef,
    currentPage,
    containerRef,
    initialPage,
    setCurrentPage,
  });

  const handlePageLoadSuccess = usePdfPageLoadHandler({
    containerRef,
    scaleCalculatedRef,
    baseScaleRef,
    originalPageWidthRef,
    originalPageHeightRef,
    manualZoom,
    setPageScale,
    setZoomLevel,
    setZoomInputValue,
    pageRefs,
    pageDimensions,
    setPositionUpdateTrigger,
    highlights,
  });

  const {
    goToNextSection,
    goToPreviousSection,
  } = usePdfSectionNavigation({
    containerRef,
    numPages,
    sections,
    pageScale,
    zoomLevel,
    isProgrammaticScrollRef,
    autoScrollRequestedRef,
    currentSectionIndex,
    setCurrentSectionIndex,
    setCurrentPage,
    findAndScrollToSection,
  });

  usePdfZoomControls({
    pageRefs,
    pageDimensions,
    setPositionUpdateTrigger,
    setManualZoom,
    zoomLevel,
    setZoomLevel,
    setZoomInputValue,
    setPageScale,
    baseScaleRef,
    setIsEditingZoom,
    zoomInputValue,
    scaleCalculatedRef,
    numPages,
    pageScale,
  });

  usePdfHighlightStyles();

  const { handleRemoveHighlightById } = usePdfHighlightRemoval({ removeHighlight });

  usePdfHighlightApplication({
    containerRef,
    numPages,
    highlights,
    currentPage,
    removeHighlight,
    handleRemoveHighlightById,
  });

  usePdfSelectionInteractions({
    containerRef,
    textActionsRef,
    isReadingRef,
    showTextActions,
    selectionRangeRef,
    buildSelectionAnchorFromRange,
    clearTextSelection,
    setShowTextActions,
    setSelectionRects,
    setSelectedText,
    setSelectionAnchor,
    setTextActionPosition,
    isReading,
  });

  const {
    handleAskBudleeAIClick,
    handleExplainClick,
    handleReadAloudClick,
    handleHighlightClick,
  } = usePdfTextActions({
    selectedText,
    selectionAnchor,
    onAskBudleeAI,
    clearTextSelection,
    isReading,
    setIsReading,
    isReadingRef,
    addHighlight,
    currentPage,
  });

  const currentSection = sections[currentSectionIndex];
  const {
    hasLearningPack,
    learningPackCountForCurrentSection,
    learningPackTitle,
    currentSectionTitle,
    handleLearningPackClick,
  } = useLearningPackState({
    onShowLearningPack,
    learningPackActivities,
    sections,
    currentPage,
    currentSection,
    currentSectionIndex,
    onSectionScroll,
  });

  // PDF loading options - memoize to avoid unnecessary reloads
  // Optimized for first-time loading with streaming and range requests (like Google Drive)
  const pdfOptions = useMemo(() => ({
    httpHeaders: authToken ? { Authorization: `Bearer ${authToken}` } : {},
    cMapUrl: PDF_CONFIG.cMapUrl,
    cMapPacked: PDF_CONFIG.cMapPacked,
    // Performance optimizations for first-time loading
    disableAutoFetch: false, // Allow automatic fetching
    disableStream: false, // Enable streaming for progressive loading (critical for first load)
    disableRange: false, // Enable range requests for faster partial loading
    // Enable caching headers
    withCredentials: false,
    verbosity: 0, // Reduce console output
    // Additional optimizations
    maxImageSize: 10 * 1024 * 1024, // Increase to 10MB to ensure all images/figures are rendered
    isEvalSupported: false, // Disable eval for security and performance
  }), [authToken]);

  const { pageNotesMap } = usePdfNotesIndex({ notes });

  const { handleSidebarPageClick } = usePdfSidebarNavigation({
    setCurrentPage,
    pageRefs,
  });

  const saveAnnotationPosition = useCallback(async (annotationId, positionTop, positionLeft) => {
    await userAnnotationPositionsAPI.updatePosition(annotationId, positionTop, positionLeft);
    if (onAnnotationPositionUpdate) {
      await onAnnotationPositionUpdate();
    }
  }, [onAnnotationPositionUpdate]);

  if (loading || parsing) {
    return <PDFLoadingState />;
  }

  if (error) {
    return <PDFErrorState error={error} onClose={onClose} />;
  }

  const showStickyHeader =
    sections.length > 0 ||
    !!onAddNote ||
    !!onViewNotes ||
    !!onViewHighlights ||
    hasLearningPack;

  return (
    <div ref={viewerRootRef} className={`flex flex-col h-full ${containerClass}`.trim()}>
      <PDFViewerHeader
        notePlacementMode={notePlacementMode}
        showStickyHeader={showStickyHeader}
        isSidebarOpen={isSidebarOpen}
        toggleSidebar={toggleSidebar}
        isSearchOpen={isSearchOpen}
        toggleSearch={toggleSearch}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        isSearching={isSearching}
        searchResults={searchResults}
        currentSearchIndex={currentSearchIndex}
        handlePrevResult={handlePrevResult}
        handleNextResult={handleNextResult}
        sections={sections}
        currentSectionIndex={currentSectionIndex}
        goToPreviousSection={goToPreviousSection}
        goToNextSection={goToNextSection}
        hasLearningPack={hasLearningPack}
        onLearningPackClick={handleLearningPackClick}
        learningPackCountForCurrentSection={learningPackCountForCurrentSection}
        learningPackTitle={learningPackTitle}
        sectionTitle={currentSectionTitle}
        onAddNote={onAddNote}
        onViewNotes={onViewNotes}
        onViewHighlights={onViewHighlights}
        pageInputValue={pageInputValue}
        handlePageInputChange={handlePageInputChange}
        handlePageInputSubmit={handlePageInputSubmit}
        numPages={numPages}
        toggleFullscreen={toggleFullscreen}
        isFullscreen={isFullscreen}
      />

      {/* PDF Viewer - Flex Container for Sidebar + Content */}
      <div className="flex flex-1 min-h-0 relative">
        <PDFSidebar
          isOpen={isSidebarOpen}
          pdfDocument={pdfDocumentProxy}
          currentPage={currentPage}
          onPageClick={handleSidebarPageClick}
        />

        {/* Scrollable Container (Only PDF content scrolls) */}
        <div
          ref={containerRef}
          data-testid="pdf-scroll"
          className={`flex-1 overflow-y-auto ${containerBgClass}`}
          style={{
            minHeight: 0, // Important for flex-1 to work correctly
            position: 'relative'
          }}
        >
          <PDFTextActions
            showTextActions={showTextActions}
            selectionRects={selectionRects}
            selectedText={selectedText}
            textActionsRef={textActionsRef}
            textActionPosition={textActionPosition}
            renderTextActions={renderTextActions}
            selectionAnchor={selectionAnchor}
            clearTextSelection={clearTextSelection}
            onAskBudleeAI={onAskBudleeAI}
            handleAskBudleeAIClick={handleAskBudleeAIClick}
            handleExplainClick={handleExplainClick}
            isReading={isReading}
            handleReadAloudClick={handleReadAloudClick}
            addHighlight={addHighlight}
            handleHighlightClick={handleHighlightClick}
          />

          <PDFDocumentContent
            contentPaddingClass={contentPaddingClass}
            numPages={numPages}
            workerReady={workerReady}
            effectivePdfUrl={effectivePdfUrl}
            pdfOptions={pdfOptions}
            onDocumentLoadSuccess={handleDocumentLoadSuccess}
            onDocumentLoadError={(error) => {
              console.error('PDF Load Error:', error);
              setError('Failed to load PDF. Please try again.');
            }}
            sectionsLength={sections.length}
            loading={loading}
          >
            <PDFPageStack
              numPages={numPages}
              pageSpacingClass={pageSpacingClass}
              pageJustifyClass={pageJustifyClass}
              pageRefs={pageRefs}
              notePlacementMode={notePlacementMode}
              onPDFClick={onPDFClick}
              pageScale={pageScale}
              textLayerReadyPagesRef={textLayerReadyPagesRef}
              highlightMatches={highlightMatches}
              handlePageLoadSuccess={handlePageLoadSuccess}
              pageDimensions={pageDimensions}
              activeFigureHighlight={activeFigureHighlight}
              chapterAnnotations={chapterAnnotations}
              userAnnotationPositions={userAnnotationPositions}
              onActivityClick={onActivityClick}
              isActivityCompleted={isActivityCompleted}
              activeAnnotationId={activeAnnotationId}
              chapterId={chapterId}
              activeBookmarks={activeBookmarks}
              onBookmarkChange={onBookmarkChange}
              saveAnnotationPosition={saveAnnotationPosition}
              setPositionUpdateTrigger={setPositionUpdateTrigger}
              positionUpdateTrigger={positionUpdateTrigger}
              pageNotesMap={pageNotesMap}
              highlightedNoteId={highlightedNoteId}
              onNoteUpdate={onNoteUpdate}
              onNotePageChange={onNotePageChange}
              containerRef={containerRef}
              setEditingNote={setEditingNote}
            />
          </PDFDocumentContent>
        </div>
      </div>

      <NoteEditModal
        editingNote={editingNote}
        setEditingNote={setEditingNote}
        onNoteUpdate={onNoteUpdate}
        onNoteDelete={onNoteDelete}
        onNotesReload={onNotesReload}
        onNoteEditComplete={onNoteEditComplete}
      />
    </div>
  );
}
